import { useState, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { X, Play } from "lucide-react";

interface VideoLightboxProps {
  youtubeId: string;
  children: React.ReactNode;
  /** Override the trigger button styling for different contexts (hero, banner, etc). */
  triggerClassName?: string;
  /** Hide the default trailing play icon (when the trigger supplies its own). */
  showIcon?: boolean;
}

const DEFAULT_TRIGGER =
  "inline-flex items-center gap-3 text-lg font-medium text-muted-foreground hover:text-primary transition-colors cursor-pointer text-left";

const VideoLightbox = ({ youtubeId, children, triggerClassName, showIcon = true }: VideoLightboxProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", handleEsc);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleEsc);
      document.body.style.overflow = "";
    };
  }, [isOpen, close]);

  const overlay = (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm"
      onClick={close}
    >
      <button
        onClick={close}
        className="absolute top-6 right-6 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors text-white"
        aria-label="Close video"
      >
        <X className="h-6 w-6" />
      </button>
      <div
        className="relative w-full max-w-5xl aspect-video"
        onClick={(e) => e.stopPropagation()}
      >
        <iframe
          src={`https://www.youtube-nocookie.com/embed/${youtubeId}?autoplay=1&rel=0`}
          title="Meshly Times Square Ad"
          allow="autoplay; encrypted-media"
          allowFullScreen
          className="w-full h-full rounded-lg shadow-2xl"
        />
      </div>
    </div>
  );

  return (
    <>
      <button onClick={open} className={triggerClassName ?? DEFAULT_TRIGGER}>
        {children}
        {showIcon && <Play className="h-4 w-4 fill-current" />}
      </button>

      {/* Portal to <body> so the fixed overlay escapes any transformed ancestor
          (e.g. the marquee's translateX, which would otherwise trap it). */}
      {isOpen && typeof document !== "undefined"
        ? createPortal(overlay, document.body)
        : null}
    </>
  );
};

export default VideoLightbox;
